import React from "react";
import { Translation, t } from "../../../core/translate";
import { Button } from "@vitalsource/vstui";
import { connect } from "react-redux";
import { selectors as brandSelectors } from "../../../../redux/brand";
import { selectors as routerSelectors } from "../../../../redux/router";
import {
  actions as userActions,
  selectors as userSelectors
} from "../../../../redux/user";
import ContainerLayout from "../container-layout";
import Link from "../../../components/link";
import styled from "styled-components";
import { toastUtils } from "../../../../utils/toast-utils";
import ChangeEmailDialog from "./change-email-dialog";
import FormAlert from "../form-alert";

const SupportText = styled.div`
  position: absolute;
  bottom: 20px;
  left: 0;
  text-align: center;
  width: 100%;
`;

const SupportLinks = styled.div`
  display: flex;
  justify-content: center;
  align-items: baseline;
  padding-top: 4px;
  & > div {
    border-right: 1px solid #000;
    padding: 0 4px;
    height: 17px;
    & > button {
      /* needed until vstui updates to next release removing padding for inline buttons */
      padding: 0;
    }
  }
  div:last-child {
    border-right: none;
  }
`;

class Verify extends React.Component {
  state = {
    resendSent: false
  };

  emailSentToast = () => {
    toastUtils.notify({
      id: "email-verification-resend-toast",
      children: (
        <span>
          {i18n.t("email-verification.email-resent", {
            email: this.props.email
          })}
        </span>
      ),
      dismissible: true,
      maxWidth: 250,
      horizontalPosition: "right",
      type: "success"
    });
  };

  resend = () => {
    if (!this.state.resendSent) {
      this.setState({ resendSent: true });
      this.props.requestEmailVerification().then(this.emailSentToast);
    } else {
      this.emailSentToast();
    }
  };

  getTitle() {
    if (this.props.expiredToken) {
      return (
        <Translation id="email-verification.verification-link-expired">
          Verification link expired
        </Translation>
      );
    } else {
      return (
        <Translation id="email-verification.please-verify-your-account">
          Please verify your account
        </Translation>
      );
    }
  }

  renderEmailSentContent() {
    return (
      <div>
        {this.state.emailChanged && (
          <FormAlert level="success" key={`success-change-${this.props.email}`}>
            <Translation
              id="registration.email-successfully-updated"
              email={this.props.email}
            >
              Email successfully updated.
            </Translation>
          </FormAlert>
        )}
        <p>
          <Translation
            id="email-verification.email-sent"
            email={<b>{this.props.email}</b>}
          >
            An email has been sent to {this.props.email}. Simply click the
            activation link in your email and you’re in!
          </Translation>
        </p>
        <p>
          <Translation id="forgot-password.resend-text">
            Didn’t get the email?
          </Translation>
          <Button naked inline onClick={this.resend}>
            <Translation id="forgot-password.resend">Resend</Translation>
          </Button>
        </p>
      </div>
    );
  }

  renderExpiredContent() {
    return (
      <div>
        <p>
          <Translation id="email-verification.verification-link-expired-details">
            Your activation link has expired.
          </Translation>
          <Button naked inline onClick={this.resend}>
            <Translation id="forgot-password.resend">Resend</Translation>
          </Button>
        </p>
      </div>
    );
  }

  renderUnauthenticatedContent() {
    return (
      <div>
        <p>
          <Translation
            id="email-verification.signin-to-verify"
            signin={
              <Link naked inline href="/user/signin">
                <Translation id="email-verification.signin-again">
                  Sign in again
                </Translation>
              </Link>
            }
          >
            Sign in again to verify your account
          </Translation>
        </p>
      </div>
    );
  }

  renderContent() {
    if (this.props.authenticated) {
      if (this.props.expiredToken) {
        return this.renderExpiredContent();
      } else {
        return this.renderEmailSentContent();
      }
    } else {
      return this.renderUnauthenticatedContent();
    }
  }

  renderSupportText() {
    return (
      <SupportText>
        <div>
          <Translation id="email-verification.didnt-get-verification">
            Didn’t get your email verification?
          </Translation>
        </div>
        <SupportLinks>
          <div>
            <Button inline href={this.props.supportUrl} target="_blank">
              <Translation id="email-verification.contact-support">
                Contact support
              </Translation>
            </Button>
          </div>
          <div>
            <Button inline onClick={() => this.setState({ changeEmail: true })}>
              <Translation id="email-verification.change-email">
                Change Email
              </Translation>
            </Button>
            {this.state.changeEmail && (
              <ChangeEmailDialog
                defaultValue={this.props.email}
                onClose={success =>
                  this.setState({ changeEmail: false, emailChanged: success })
                }
              />
            )}
          </div>
          <div>
            <Link
              inline
              href="/user/signin"
              onClick={this.props.signOutAndRedirect}
            >
              <Translation id="email-verification.signin-again">
                Sign in again
              </Translation>
            </Link>
          </div>
        </SupportLinks>
      </SupportText>
    );
  }

  render() {
    return (
      <ContainerLayout
        graphic={this.props.expiredToken ? "sad" : "signin"}
        title={this.getTitle()}
      >
        {this.renderContent()}
        {this.renderSupportText()}
      </ContainerLayout>
    );
  }
}

const mapStateToProps = state => ({
  supportUrl: brandSelectors.getBrandValue(state, "Support.url"),
  authenticated: userSelectors.isAuthenticated(state),
  email: userSelectors.getUserEmail(state),
  expiredToken: routerSelectors.getQueryParam(state, "expired") === "true",
  forgotEnabled: brandSelectors.getBrandValue(
    state,
    "ForgotPassword.enabled",
    true
  )
});

const mapDispatchToProps = dispatch => ({
  requestEmailVerification: () =>
    dispatch(userActions.requestEmailVerification()),
  signOutAndRedirect: () => dispatch(userActions.signOut({}, "/user/signin"))
});

const ConnectedVerify = connect(
  mapStateToProps,
  mapDispatchToProps
)(Verify);

export default ConnectedVerify;
